if (!customElements.get('product-form')) {
  /**
   * Custom element that manages product form submissions and cart AJAX updates.
   * @extends HTMLElement
   */
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Handles form submission via AJAX and updates cart/notification components.
       * @param {SubmitEvent} evt
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const sign = this.closest('.shopify-section')?.querySelector('fw-personalized-sign');
        if (sign && !sign.validate()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        if (sign && sign.enabled) {
          const quantity = Number(formData.get('quantity') || 1);
          const properties = {};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          }
          const signProperties = sign.dataset.method === 'print' ? { 'Personalization': sign.nameValue, 'Personalization method': 'Printed on center panel' } : { 'Name': sign.nameValue, ...(sign.ageInput ? { 'Age': sign.ageValue } : {}), 'Sign text': sign.signText };
          const main = { id: formData.get('id'), quantity, properties };
          const items = sign.dataset.standalone === 'true' || sign.dataset.method === 'print'
            ? [{ ...main, properties: { ...properties, ...signProperties } }]
            : [main, { id: sign.dataset.variant, quantity, properties: { ...signProperties, 'For backdrop': sign.dataset.backdrop } }];
          const payload = { items };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map(section => section.id);
            payload.sections_url = window.location.pathname;
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      /**
       * Displays or hides the form error message.
       * @param {string|boolean} [errorMessage=false]
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

/**
 * Custom element for displaying a sticky cart modal on scroll.
 * @extends HTMLElement
 */
class stickyCartModal extends HTMLElement {
  constructor() {
    super();
    window.addEventListener('scroll', this.update.bind(this));
    this.update();
    this.querySelector('.sticky-cart-button').addEventListener('click', this.onClickHandler.bind(this));
    this.querySelector('.sticky-cart-button').addEventListener('keydown', this.onClickKeydownHandler.bind(this));
  }

  /** Updates the visibility of the sticky bar based on scroll position. */
  update(){
    const sectionId = this.dataset.section;
    const buttonContainer = document.getElementById(`product-form-${sectionId}`);
    const sect = this.closest('.shopify-section, .popup-modal__content__data');
    const hasSelectOption = sect?.querySelector('select-option-js');
    const customAddButton = buttonContainer?.querySelector('.select-options-button');
    const addButton = (hasSelectOption && customAddButton) ? customAddButton : buttonContainer?.querySelector('[name="add"]');
    const rangeToShowModal = this.getTop(addButton) + addButton.clientHeight;

    if(rangeToShowModal <= 0) return false;
    if(window.scrollY >= rangeToShowModal && !this.classList.contains('show-modal')){
      this.classList.add('show-modal');
      document.querySelector('body').classList.add('modal__sticky-cart');
      document.querySelector('.footer__content').classList.add('sticky-cart-modal_bottom_padding');
    }
    if(window.scrollY < rangeToShowModal && this.classList.contains('show-modal')){
      this.classList.remove('show-modal');
      document.querySelector('body').classList.remove('modal__sticky-cart');
    }
  }

  onClickKeydownHandler(event){
    if(event.code.toUpperCase() !== 'ENTER') return false;
    this.onClickHandler();
  }

  /** Smoothly scrolls the window to the main product section. */
  onClickHandler(){
    var item = document.querySelector(`.product__column__content [id*="variant-selects-"]`),
        btn_item = document.getElementById("product-container"),
        _y = (item ? this.getTop(item) : this.getTop(btn_item)) - 20;
    this.scrollTo(_y,700);
  }

  /**
   * Recursively calculates the element's top position relative to the document.
   * @param {HTMLElement} el
   * @returns {number}
   */
  getTop(el) {
    return el.offsetTop + (el.offsetParent && this.getTop(el.offsetParent));
  }

  /**
   * Scrolls the page to a specific Y position.
   * @param {number} to
   * @param {number} duration
   */
  scrollTo(to, duration) {
    const element = document.scrollingElement || document.documentElement,
          start = element.scrollTop,
          change = to - start,
    animateScroll = function() {
      element.scrollTop = to;
    };
    animateScroll();
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);

if (!customElements.get('fw-personalized-sign')) {
  customElements.define('fw-personalized-sign', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.scope = this.closest('.shopify-section');
      this.nameInput = this.querySelector('[data-sign-name]');
      this.toggle = this.querySelector('[data-sign-toggle]');
      this.variants = JSON.parse(this.querySelector('[data-sign-variants]').textContent);
      this.setupAge();
      this.updateBound = () => this.update();
      this.scope.addEventListener('input', this.updateBound);
      this.scope.addEventListener('change', this.updateBound);
      this.observer = new MutationObserver(this.updateBound);
      this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      this.update();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.scope?.removeEventListener('input', this.updateBound);
      this.scope?.removeEventListener('change', this.updateBound);
      this.ready = false;
    }
    setupAge() {
      this.ageInput = this.querySelector('[data-sign-age]');
      if (this.ageInput || !(this.dataset.design || '').includes('{age}') || !this.nameInput) return;
      const id = (this.nameInput.id || 'SignName') + '-age';
      const wrap = document.createElement('div');
      wrap.className = 'fw-sign-age';
      wrap.innerHTML = '<label for="' + id + '" style="display:block;font-weight:600;margin:16px 0 9px">Age</label><input type="text" id="' + id + '" data-sign-age inputmode="numeric" pattern="[0-9]{1,3}" maxlength="3" autocomplete="off" placeholder="e.g. 4" style="display:block;width:100%;min-height:46px;background:#fff;border:1px solid #c9beb5;border-radius:8px;padding:11px 12px;color:#302a25;font-size:16px">';
      this.nameInput.insertAdjacentElement('afterend', wrap);
      this.ageInput = wrap.querySelector('input');
      this.ageInput.addEventListener('input', () => { const v = this.ageInput.value.replace(/\D/g, '').slice(0, 3); if (v !== this.ageInput.value) this.ageInput.value = v; });
      const example = this.dataset.exampleAge || '4';
      this.querySelectorAll('[id^="SignHint"] strong').forEach(el => { el.textContent = el.textContent.replaceAll('{age}', example); });
    }
    get enabled() { return this.dataset.standalone === 'true' || this.toggle?.checked; }
    get nameValue() { return this.nameInput.value.trim().replace(/\s+/g, ' '); }
    get ageValue() { return this.ageInput ? this.ageInput.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 3) : ''; }
    get signText() { if (this.dataset.method === 'print') return this.nameValue; const n = this.nameValue; const design = this.dataset.design || ''; if (!n) return ''; let text = design.includes('{name}') ? design.replaceAll('{name}', n) : n + (/[sS]$/.test(n) ? '’ ' : '’s ') + design; if (this.ageInput) text = text.replaceAll('{age}', this.ageValue || '…'); return text; }
    setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }
    update() {
      const enabled = this.enabled;
      this.querySelector('[data-sign-fields]').hidden = !enabled;
      this.nameInput.required = enabled;
      this.nameInput.disabled = !enabled;
      this.nameInput.setCustomValidity('');
      if (this.ageInput) { this.ageInput.required = enabled; this.ageInput.disabled = !enabled; this.ageInput.setCustomValidity(''); }
      this.setText(this.querySelector('[data-sign-preview]'), this.signText || (this.dataset.method === 'print' ? 'Your wording will appear here.' : 'Your personalized sign text will appear here.'));
      const form = this.scope.querySelector('product-form form');
      const variant = this.variants.find(v => String(v.id) === form?.querySelector('[name="id"]')?.value) || this.variants[0];
      const quantity = Math.max(1, Number(this.scope.querySelector('[name="quantity"]')?.value || 1));
      const addon = enabled && this.dataset.standalone !== 'true' ? Number(this.dataset.price) : 0;
      const total = ((variant?.price || 0) + addon) * quantity / 100;
      this.setText(this.querySelector('[data-sign-total]'), new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency }).format(total));
      this.setText(this.querySelector('[data-sign-quantity]'), this.dataset.method === 'print' ? (enabled ? 'Printed personalization included' : 'Without personalization') : this.dataset.standalone === 'true' ? 'Personalized sign' : enabled ? 'Set + one extra sign per set' : 'Standard sign included');
      const shipping = this.scope.querySelector('[data-fw-shipping]');
      this.setText(shipping, variant?.available === false ? 'Currently out of stock' : enabled ? 'Personalization adds 2–3 business days' : 'Made to order — ships next business day');
    }
    validate() {
      if (!this.enabled) return true;
      if (!this.nameValue) this.nameInput.setCustomValidity(this.dataset.method === 'print' ? 'Please enter the wording to print on your backdrop.' : 'Please enter the name for your personalized sign.');
      if (!this.nameInput.checkValidity()) { this.nameInput.reportValidity(); this.nameInput.focus(); return false; }
      if (this.ageInput && !this.ageValue) { this.ageInput.setCustomValidity('Please enter the age for your sign.'); this.ageInput.reportValidity(); this.ageInput.focus(); return false; }
      return true;
    }
  });
}
